import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Logout, Person, Key, SupportAgent, Help, History } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { getRoleNames } from '../utils/roleUtils';
import ApplicationUserGuide from '../components/ApplicationUserGuide';
import ChangeLog from '../components/ChangeLog';

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [guideDialogOpen, setGuideDialogOpen] = React.useState(false);
  const [changeLogDialogOpen, setChangeLogDialogOpen] = React.useState(false);
  const { user, logout, getUser, token, isAuthenticated } = useAuthStore();

  // Fetch user data when component mounts
  useEffect(() => {
    console.log('MainLayout mounted, token:', token);
    console.log('Is authenticated:', isAuthenticated);
    
    // Always try to fetch user data on mount
    const fetchUserData = async () => {
      try {
        console.log('Fetching user data...');
        await getUser();
        console.log('User data fetched successfully');
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [getUser, token, isAuthenticated]);

  useEffect(() => {
    console.log('Current user data:', user);
  }, [user]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Electronic BIRA
          </Typography>
          
          {/* User name and icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user && (
              <Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography 
                  variant="body1" 
                  color="white"
                  sx={{ 
                    fontWeight: 'medium',
                    lineHeight: 1.2
                  }}
                >
                  {user.full_name || 'User'}
                </Typography>
                <Typography 
                  variant="body2"
                  sx={{ 
                    opacity: 0.8,
                    lineHeight: 0.75
                  }}
                >
                  {user.email || ''}
                </Typography>
              </Box>
            )}
            
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Person />
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {user ? (
                <MenuItem disabled>
                  {user.full_name || 'User'} ({user.email || ''})
                </MenuItem>
              ) : (
                <MenuItem disabled>Loading user data...</MenuItem>
              )}
              <Divider />
              <MenuItem onClick={() => { handleClose(); navigate('/ticketing'); }}>
                <ListItemIcon>
                  <SupportAgent fontSize="small" />
                </ListItemIcon>
                Tickets
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); setGuideDialogOpen(true); }}>
                <ListItemIcon>
                  <Help fontSize="small" />
                </ListItemIcon>
                User Guide
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); setChangeLogDialogOpen(true); }}>
                <ListItemIcon>
                  <History fontSize="small" />
                </ListItemIcon>
                Change Log
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleClose(); navigate('/change-password'); }}>
                <ListItemIcon>
                  <Key fontSize="small" />
                </ListItemIcon>
                Change Password
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: '100%',
          mt: '64px' // Add margin top to account for AppBar height
        }}
      >
        {children}
      </Box>

      {/* Application User Guide Dialog */}
      <ApplicationUserGuide 
        open={guideDialogOpen} 
        onClose={() => setGuideDialogOpen(false)} 
      />

      {/* Change Log Dialog */}
      <ChangeLog 
        open={changeLogDialogOpen} 
        onClose={() => setChangeLogDialogOpen(false)} 
      />
    </Box>
  );
}
